import { useState } from "react";
import { useForms, type FormRecord } from "../hooks/useForms";
import { escapeHtml } from "../lib/format";

function formatFormsDate(dateStr?: string): string {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
}

// Ported from renderFormLink().
function FormLink({ url, formType, label }: { url?: string; formType?: string; label: string }) {
  if (!url) return <span className="form-cell-empty">—</span>;
  let className = "form-link";
  let icon = <i className="ph ph-file" />;
  const isR2Url = url.includes("downloads.atxfilmlab.com") || url.includes(".r2.dev");
  if (formType === "r2-html" || formType === "html") {
    className += " html-form";
    icon = <i className="ph ph-note" />;
  } else if (formType === "gdrive") {
    className += " gdrive-form";
    icon = <i className="ph ph-folder" />;
  } else if (formType === "easypost" && !isR2Url) {
    className += " easypost-form";
    icon = <>📦</>;
  } else if (isR2Url) {
    className += " r2-form";
    icon = <i className="ph ph-cloud" />;
  } else if (formType === "png") {
    icon = <i className="ph ph-image" />;
  }
  let downloadUrl = url;
  if (formType === "gdrive" && url.includes("/file/d/")) {
    const fileId = url.match(/\/file\/d\/([^/]+)/)?.[1];
    if (fileId) downloadUrl = `https://drive.google.com/uc?export=download&id=${fileId}`;
  }
  return (
    <a href={downloadUrl} target="_blank" rel="noreferrer" className={className} onClick={(e) => e.stopPropagation()}>
      {icon} {label}
    </a>
  );
}

export function FormsTab({ active }: { active: boolean }) {
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [params, setParams] = useState({ search: "", startDate: "", endDate: "", showAll: false });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lastIdx, setLastIdx] = useState<number | null>(null);

  const { data } = useForms(params, active);
  const records = data?.records ?? [];

  const toggle = (invoiceId: string, idx: number, shift: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && lastIdx !== null && lastIdx !== idx) {
        const [s, e] = idx < lastIdx ? [idx, lastIdx] : [lastIdx, idx];
        const target = !next.has(invoiceId);
        for (let i = s; i <= e; i++) {
          const id = records[i]?.invoiceId;
          if (!id) continue;
          if (target) next.add(id);
          else next.delete(id);
        }
      } else if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
    setLastIdx(idx);
  };

  const toggleAll = (checked: boolean) => setSelected(checked ? new Set(records.map((r) => r.invoiceId)) : new Set());
  const allChecked = records.length > 0 && selected.size === records.length;

  async function bulkDownloadMailback() {
    const sel = records.filter((r) => selected.has(r.invoiceId));
    if (!sel.length) return;
    let success = 0;
    for (let i = 0; i < sel.length; i++) {
      const r = sel[i];
      const r2Key = r.mailbackR2Key;
      const fallbackUrl = r.mailbackLabel;
      if (!r2Key && !fallbackUrl) continue;
      const customerName = (r.customerName || "unknown").replace(/[^a-zA-Z0-9]/g, "_");
      const filename = `mailback_${customerName}_${r.invoiceId.slice(-8)}.png`;
      try {
        let blob: Blob | null = null;
        if (r2Key) {
          const resp = await fetch(`/api/download-r2-file?key=${encodeURIComponent(r2Key)}&filename=${encodeURIComponent(filename)}`);
          if (resp.ok) blob = await resp.blob();
        }
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
          success++;
        } else if (fallbackUrl) {
          window.open(fallbackUrl, "_blank");
          success++;
        }
      } catch (e) {
        console.error("Download failed:", e);
      }
      if (i < sel.length - 1) await new Promise((res) => setTimeout(res, 500));
    }
    console.log(`Downloaded ${success} mailback label(s)`);
  }

  // HTML drop-off form print preview (ported from bulkPrintForms()).
  async function bulkPrint() {
    const sel = records.filter((r) => selected.has(r.invoiceId));
    const htmlForms = sel
      .filter((r) => {
        const u = r.dropOffForm;
        return u && (u.endsWith(".html") || (u.includes("r2.dev") && !u.endsWith(".pdf")));
      })
      .map((r) => ({ url: r.dropOffForm!, invoiceId: r.invoiceId, customerName: r.customerName || "Unknown" }));
    if (!htmlForms.length) {
      alert("No HTML forms found in selection. Only drop-off forms can be printed.");
      return;
    }
    const overlay = document.createElement("div");
    overlay.className = "print-preview-overlay";
    overlay.id = "printPreviewOverlay";
    overlay.innerHTML = `
      <div class="print-preview-header">
        <h2>Print Preview - ${htmlForms.length} Form(s)</h2>
        <div class="print-preview-actions">
          <button class="print-btn" id="__printAll">🖨️ Print All</button>
          <button class="close-btn" id="__printClose"><i class="ph ph-x"></i> Close</button>
        </div>
      </div>
      <div class="print-preview-content">
        <iframe id="printFrame" class="print-preview-frame"></iframe>
      </div>`;
    document.body.appendChild(overlay);
    const printFrame = document.getElementById("printFrame") as HTMLIFrameElement;
    overlay.querySelector("#__printClose")?.addEventListener("click", () => overlay.remove());
    overlay.querySelector("#__printAll")?.addEventListener("click", () => printFrame.contentWindow?.print());
    const printDoc = printFrame.contentDocument || printFrame.contentWindow!.document;
    let combinedHtml = `<!DOCTYPE html><html><head><title>Print Forms</title><style>@page { size: letter; margin: 0.25in; } * { box-sizing: border-box; } body { margin:0; padding:0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; } .form-page { width:100%; page-break-after: always; } .form-page:last-child { page-break-after: auto; }</style></head><body>`;
    for (const form of htmlForms) {
      try {
        const resp = await fetch(`/api/fetch-form-content?url=${encodeURIComponent(form.url)}`);
        const fdata = (await resp.json()) as { content?: string; error?: string };
        if (fdata.error) throw new Error(fdata.error);
        const html = fdata.content || "";
        const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        const styleMatch = html.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
        const formContent = bodyMatch ? bodyMatch[1] : html;
        const styles = styleMatch ? styleMatch.join("") : "";
        combinedHtml += `<div class="form-page">${styles}${formContent}</div>`;
      } catch (err) {
        combinedHtml += `<div class="form-page"><div style="padding:20px;color:red;">Error loading form for ${escapeHtml(form.customerName)}<br><small>${escapeHtml(form.url)}</small></div></div>`;
      }
    }
    combinedHtml += `</body></html>`;
    printDoc.open();
    printDoc.write(combinedHtml);
    printDoc.close();
  }

  return (
    <div className="tab-content" style={{ display: active ? undefined : "none" }}>
      <div className="forms-controls">
        <input type="text" placeholder="Search by customer name..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <label style={{ marginLeft: 10, color: "#666" }}>From:</label>
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <label style={{ marginLeft: 5, color: "#666" }}>To:</label>
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        <button onClick={() => setParams({ search, startDate, endDate, showAll: false })}>Apply</button>
        <button
          onClick={() => {
            setSearch("");
            setStartDate("");
            setEndDate("");
            setParams({ search: "", startDate: "", endDate: "", showAll: false });
          }}
        >
          Clear
        </button>
        <button onClick={() => setParams({ search, startDate, endDate, showAll: true })} style={{ backgroundColor: "#37358F" }}>
          Show All
        </button>
      </div>

      <div className="bulk-actions-bar" style={{ display: selected.size > 0 ? undefined : "none" }}>
        <span>{selected.size} selected</span>
        <button onClick={bulkDownloadMailback} className="bulk-action-btn">
          <i className="ph ph-package" /> Download Mailback Labels
        </button>
        <button onClick={bulkPrint} className="bulk-action-btn print-btn">
          <i className="ph ph-printer" /> Print Forms (HTML)
        </button>
        <button onClick={() => setSelected(new Set())} className="bulk-action-btn cancel-btn">
          <i className="ph ph-x" /> Clear Selection
        </button>
      </div>

      <div className="table-container">
        <table id="formsTable">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} />
              </th>
              <th style={{ width: 100 }}>Date</th>
              <th>Customer Name</th>
              <th style={{ width: 120 }}>Drop Off Form</th>
              <th style={{ width: 120 }}>Mailback Label</th>
              <th style={{ width: 120 }}>Mail In Label</th>
            </tr>
          </thead>
          <tbody id="formsTableBody">
            {records.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 40, color: "#666" }}>
                  No forms found
                </td>
              </tr>
            ) : (
              records.map((record: FormRecord, idx) => (
                <tr key={record.invoiceId} className={selected.has(record.invoiceId) ? "selected" : ""}>
                  <td>
                    <input
                      type="checkbox"
                      className="forms-row-checkbox"
                      checked={selected.has(record.invoiceId)}
                      onChange={() => {}}
                      onClick={(e) => toggle(record.invoiceId, idx, (e as React.MouseEvent).shiftKey)}
                    />
                  </td>
                  <td>{formatFormsDate(record.timestamp)}</td>
                  <td>
                    <a
                      href={`/users/${encodeURIComponent(record.customerId || "")}`}
                      className="customer-name-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {record.customerName || "Unknown"}
                    </a>
                  </td>
                  <td>
                    <FormLink url={record.dropOffForm} formType={record.dropOffFormType} label="Drop Off" />
                  </td>
                  <td>
                    <FormLink url={record.mailbackLabel} formType={record.mailbackLabelType} label="Mailback" />
                  </td>
                  <td>
                    <FormLink url={record.mailInLabel} formType={record.mailInLabelType} label="Mail In" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
